/**
 * GRACY — theme.js (ES module, deferred)
 * Native Web Components only. Every component works with the theme editor:
 * custom elements re-initialise themselves when a section is re-rendered,
 * and editor events (shopify:block:select …) are handled explicitly.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const inEditor = Boolean(window.Shopify && window.Shopify.designMode);

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, summary';

/* -------------------------------------------------------------------------- */
/* Scroll lock (counts nested locks: drawer + modal)                          */
/* -------------------------------------------------------------------------- */
let lockCount = 0;
const lockScroll = () => {
  lockCount += 1;
  document.documentElement.classList.add('is-scroll-locked');
};
const unlockScroll = () => {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) document.documentElement.classList.remove('is-scroll-locked');
};

/* -------------------------------------------------------------------------- */
/* Reveal on scroll ([data-reveal], children [data-reveal-item] staggered)    */
/* -------------------------------------------------------------------------- */
const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            revealElement(entry.target);
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
      )
    : null;

function revealElement(el) {
  el.querySelectorAll('[data-reveal-item]').forEach((item, index) => {
    item.style.setProperty('--reveal-index', String(index));
  });
  el.classList.add('is-revealed');
}

function scanReveal(root = document) {
  root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
    if (!revealObserver || inEditor || reduceMotion.matches) revealElement(el);
    else revealObserver.observe(el);
  });
}

/* -------------------------------------------------------------------------- */
/* <sticky-header data-mode="none|always|scroll-up">                          */
/* Publishes --header-height on :root; toggles .is-scrolled / .is-hidden.     */
/* -------------------------------------------------------------------------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode || 'always';
    this.section = this.closest('.shopify-section') || this;
    this.lastY = window.scrollY;
    this.onScroll = this.onScroll.bind(this);

    this.resizeObserver = new ResizeObserver(() => this.publishHeight());
    this.resizeObserver.observe(this);
    this.publishHeight();

    if (this.mode !== 'none') {
      this.section.classList.add('header-section--sticky');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
    this.section?.classList.remove('header-section--sticky', 'is-hidden');
  }

  publishHeight() {
    document.documentElement.style.setProperty('--header-height', `${Math.round(this.offsetHeight)}px`);
  }

  onScroll() {
    const y = window.scrollY;
    const threshold = this.offsetTop + this.offsetHeight;
    this.classList.toggle('is-scrolled', y > 4);
    this.section.classList.toggle('is-scrolled', y > 4);

    if (this.mode === 'scroll-up' && !document.documentElement.classList.contains('is-scroll-locked')) {
      const goingDown = y > this.lastY && y > threshold;
      this.section.classList.toggle('is-hidden', goingDown);
    }
    this.lastY = y;
  }
}

/* -------------------------------------------------------------------------- */
/* <drawer-panel id="…"> — generic drawer / modal                             */
/* Openers: [aria-controls="<id>"][data-drawer-toggle]. Focus trap, Esc,      */
/* overlay click, scroll lock, returns focus to the opener.                   */
/* -------------------------------------------------------------------------- */
class DrawerPanel extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', this.getAttribute('role') || 'dialog');
    this.setAttribute('aria-modal', 'true');
    if (!this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'true');
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]') || event.target === this.querySelector('.drawer__overlay')) {
        this.close();
      }
    });
  }

  get isOpen() {
    return this.getAttribute('aria-hidden') === 'false';
  }

  open(opener) {
    if (this.isOpen) return;
    this.opener = opener || document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    lockScroll();
    document.addEventListener('keydown', this.onKeydown);
    requestAnimationFrame(() => {
      const target = this.querySelector('[autofocus]') || this.querySelector('.drawer__panel') || this;
      target.focus({ preventScroll: true });
    });
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    unlockScroll();
    document.removeEventListener('keydown', this.onKeydown);
    if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  toggle(opener) {
    this.isOpen ? this.close() : this.open(opener);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  disconnectedCallback() {
    if (this.isOpen) unlockScroll();
    document.removeEventListener('keydown', this.onKeydown);
  }
}

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-drawer-toggle]');
  if (!toggle) return;
  const drawer = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!(drawer instanceof DrawerPanel)) return;
  event.preventDefault();
  drawer.toggle(toggle);
});

/* -------------------------------------------------------------------------- */
/* <details-dropdown> — header submenus built on <details>                    */
/* Opens on hover (pointer devices), closes on outside click / Esc / focus out */
/* -------------------------------------------------------------------------- */
class DetailsDropdown extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;
    this.summary = this.details.querySelector('summary');
    const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.addEventListener('mouseenter', () => hoverable.matches && this.setOpen(true));
    this.addEventListener('mouseleave', () => hoverable.matches && this.setOpen(false));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.setOpen(false);
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.details.open) {
        this.setOpen(false);
        this.summary.focus();
      }
    });
    this.onOutside = (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    };
    document.addEventListener('click', this.onOutside);
    this.details.addEventListener('toggle', () => {
      this.summary.setAttribute('aria-expanded', String(this.details.open));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside);
  }

  setOpen(open) {
    if (this.details.open !== open) this.details.open = open;
  }
}

/* -------------------------------------------------------------------------- */
/* <accordion-item> — animates a native <details>; exclusive groups use the   */
/* native `name` attribute on <details>.                                      */
/* -------------------------------------------------------------------------- */
class AccordionItem extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    this.content = this.details?.querySelector('.accordion__content');
    if (!this.summary || !this.content) return;
    this.summary.addEventListener('click', (event) => this.onClick(event));
  }

  onClick(event) {
    if (reduceMotion.matches || inEditor) return;
    event.preventDefault();
    if (this.details.open) this.collapse();
    else this.expand();
  }

  expand() {
    // Close siblings sharing the same exclusive name.
    const name = this.details.getAttribute('name');
    if (name) {
      document.querySelectorAll(`details[name="${CSS.escape(name)}"][open]`).forEach((other) => {
        if (other !== this.details) other.closest('accordion-item')?.collapse();
      });
    }
    this.details.open = true;
    const height = this.content.scrollHeight;
    this.content.animate([{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }], {
      duration: 320,
      easing: 'cubic-bezier(.2,.7,.2,1)',
    });
  }

  collapse() {
    const height = this.content.scrollHeight;
    const animation = this.content.animate([{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
      duration: 260,
      easing: 'cubic-bezier(.4,0,.2,1)',
    });
    animation.onfinish = () => {
      this.details.open = false;
    };
  }

  open() {
    this.details.open = true;
  }
}

/* -------------------------------------------------------------------------- */
/* <carousel-slider data-autoplay="5000" data-loop>                           */
/* Track: [data-carousel-track] (scroll-snap). Slides: direct children.       */
/* Controls: [data-carousel-prev] [data-carousel-next] [data-carousel-dots].  */
/* -------------------------------------------------------------------------- */
class CarouselSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.slides = [...this.track.children].filter((el) => !['STYLE', 'SCRIPT', 'TEMPLATE'].includes(el.tagName));
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.dots = this.querySelector('[data-carousel-dots]');
    this.status = this.querySelector('[data-carousel-status]');
    this.loop = this.hasAttribute('data-loop');
    this.delay = Number(this.dataset.autoplay || 0);

    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.go(this.index + 1);
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
    });

    this.buildDots();
    this.update();

    if (this.delay && !reduceMotion.matches && !inEditor) {
      this.play();
      ['mouseenter', 'focusin', 'touchstart'].forEach((type) => this.addEventListener(type, () => this.pause(), { passive: true }));
      ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => this.play()));
      document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.play()));
    }

    // Theme editor: scroll the selected block into view and hold autoplay.
    this.onBlockSelect = (event) => {
      const i = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target);
      if (i === -1) return;
      this.pause();
      this.go(i, 'instant');
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  get perView() {
    if (!this.slides.length) return 1;
    return Math.max(1, Math.round(this.track.clientWidth / this.slides[0].getBoundingClientRect().width));
  }

  get pages() {
    return Math.max(1, this.slides.length - this.perView + 1);
  }

  get index() {
    const left = this.track.scrollLeft;
    let closest = 0;
    let distance = Infinity;
    this.slides.forEach((slide, i) => {
      const d = Math.abs(slide.offsetLeft - this.track.offsetLeft - left);
      if (d < distance) {
        distance = d;
        closest = i;
      }
    });
    return closest;
  }

  go(target, behavior = 'smooth') {
    let i = target;
    if (i < 0) i = this.loop ? this.pages - 1 : 0;
    if (i > this.pages - 1) i = this.loop ? 0 : this.pages - 1;
    const slide = this.slides[i];
    if (!slide) return;
    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: reduceMotion.matches ? 'instant' : behavior,
    });
  }

  buildDots() {
    if (!this.dots) return;
    this.dots.innerHTML = '';
    const label = this.dots.dataset.label || 'Slide';
    for (let i = 0; i < this.pages; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'carousel__dot';
      dot.setAttribute('aria-label', `${label} ${i + 1}`);
      dot.addEventListener('click', () => this.go(i));
      this.dots.append(dot);
    }
  }

  onScroll() {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.update());
  }

  update() {
    const i = this.index;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    if (this.prev) this.prev.disabled = !this.loop && atStart;
    if (this.next) this.next.disabled = !this.loop && atEnd;
    this.toggleAttribute('data-static', this.pages <= 1);
    [...(this.dots?.children || [])].forEach((dot, d) => dot.setAttribute('aria-current', String(d === Math.min(i, this.pages - 1))));
    this.slides.forEach((slide, s) => {
      const visible = s >= i && s < i + this.perView;
      slide.toggleAttribute('inert', !visible && this.hasAttribute('data-inert-hidden'));
    });
    if (this.status) this.status.textContent = `${i + 1} / ${this.slides.length}`;
  }

  play() {
    if (!this.delay || this.timer) return;
    this.timer = window.setInterval(() => {
      const i = this.index;
      this.go(i >= this.pages - 1 ? 0 : i + 1);
    }, this.delay);
  }

  pause() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}

/* -------------------------------------------------------------------------- */
/* <localization-form> — submits on select change                              */
/* -------------------------------------------------------------------------- */
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-autosubmit]')) this.querySelector('form')?.submit();
    });
  }
}

/* -------------------------------------------------------------------------- */
/* <video-facade> — injects the YouTube/Vimeo iframe only on click            */
/* -------------------------------------------------------------------------- */
class VideoFacade extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', () => this.load(), { once: true });
  }

  load() {
    const template = this.querySelector('template');
    if (!template) return;
    this.replaceChildren(template.content.cloneNode(true));
    this.querySelector('iframe')?.focus();
  }
}

/* -------------------------------------------------------------------------- */
/* <share-button> — native share sheet, else copy link                         */
/* -------------------------------------------------------------------------- */
class ShareButton extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-share-copy]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const { url, title } = this.dataset;
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share({ url, title });
          return;
        } catch {
          /* cancelled: fall back to copy */
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        const status = this.querySelector('[data-share-status]');
        if (status) status.textContent = button.dataset.copied;
        button.classList.add('is-copied');
        setTimeout(() => button.classList.remove('is-copied'), 2000);
      } catch {
        window.prompt('', url);
      }
    });
  }
}

/* -------------------------------------------------------------------------- */
/* <marquee-band> — clones the item group so the CSS loop is seamless          */
/* -------------------------------------------------------------------------- */
class MarqueeBand extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('.marquee__track');
    const group = track?.querySelector('.marquee__group');
    if (!group || track.querySelectorAll('.marquee__group').length > 1) return;
    const clone = group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
    track.append(clone);
  }
}

/* -------------------------------------------------------------------------- */
/* <product-recommendations> — Product Recommendations API                    */
/* -------------------------------------------------------------------------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('.item-grid') || !this.dataset.url) return;
    const load = async () => {
      try {
        const html = await (await fetch(this.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          scanReveal(this);
        }
      } catch {
        /* recommendations are optional */
      }
    };
    if (!('IntersectionObserver' in window)) return void load();
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      load();
    }, { rootMargin: '0px 0px 400px 0px' });
    io.observe(this);
  }
}

/* -------------------------------------------------------------------------- */
/* <recently-viewed> — cards for handles stored by the product page           */
/* -------------------------------------------------------------------------- */
class RecentlyViewed extends HTMLElement {
  async connectedCallback() {
    let handles = [];
    try {
      handles = JSON.parse(localStorage.getItem('gracy:recently-viewed') || '[]');
    } catch {
      return;
    }
    handles = handles.filter((h) => h && h !== this.dataset.exclude).slice(0, Number(this.dataset.limit || 4));
    if (!handles.length) return;
    const root = this.dataset.root || '/';
    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=product-card-sra`);
          if (!res.ok) return '';
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          return doc.querySelector('.card') ? `<div class="recently-viewed__item">${doc.querySelector('.card').outerHTML}</div>` : '';
        } catch {
          return '';
        }
      }),
    );
    const html = cards.join('');
    if (!html) return;
    this.innerHTML = html;
    this.closest('.section')?.removeAttribute('hidden');
  }
}

/* -------------------------------------------------------------------------- */
/* <routine-add-all> — adds one or every routine step to the cart              */
/* -------------------------------------------------------------------------- */
class RoutineAddAll extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const single = event.target.closest('[data-routine-add]');
      const all = event.target.closest('[data-routine-all]');
      if (single) this.add([{ id: Number(single.dataset.routineAdd), quantity: 1 }], single);
      if (all) {
        const items = [...this.querySelectorAll('.routine-step[data-variant-id]')]
          .filter((step) => step.dataset.available === 'true' && step.dataset.variantId)
          .map((step) => ({ id: Number(step.dataset.variantId), quantity: 1 }));
        if (items.length) this.add(items, all);
      }
    });
  }

  async add(items, button) {
    const error = this.querySelector('[data-routine-error]');
    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message);
      document.dispatchEvent(new CustomEvent('cart:add', { detail: { item: data, source: button } }));
    } catch (e) {
      if (error) {
        error.textContent = e.message || this.dataset.error;
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

/* -------------------------------------------------------------------------- */
/* <predictive-search> Predictive Search API rendered by the                  */
/* "predictive-search" section; combobox keyboard navigation (WAI-ARIA 1.2).  */
/* -------------------------------------------------------------------------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.reset = this.querySelector('[data-predictive-reset]');
    this.quick = this.querySelector('[data-predictive-quick]');
    this.cache = new Map();
    if (!this.input) return;
    this.input.addEventListener('input', () => this.onInput());
    this.reset?.addEventListener('click', () => {
      requestAnimationFrame(() => {
        this.onInput();
        this.input.focus();
      });
    });
    if (this.dataset.enabled !== 'true' || !this.results) return;
    let timer;
    this.debounced = () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.search(this.input.value.trim()), 260);
    };
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  onInput() {
    const term = this.input.value.trim();
    if (this.reset) this.reset.hidden = term === '';
    if (this.quick) this.quick.hidden = term !== '';
    if (!this.debounced) return;
    if (!term) {
      this.abort?.abort();
      this.close();
      return;
    }
    this.debounced();
  }

  url(term) {
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page,article,query',
      'resources[limit]': this.dataset.limit || '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': this.dataset.unavailable || 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
      section_id: 'predictive-search',
    });
    return `${window.Shopify?.routes?.root || '/'}search/suggest?${params}`;
  }

  async search(term) {
    if (!term) return;
    if (this.cache.has(term)) return this.render(this.cache.get(term));
    this.abort?.abort();
    this.abort = new AbortController();
    this.results.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(this.url(term), { signal: this.abort.signal });
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const html = doc.querySelector('[data-predictive]')?.outerHTML || '';
      this.cache.set(term, html);
      if (this.input.value.trim() === term) this.render(html);
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.results.removeAttribute('aria-busy');
    }
  }

  render(html) {
    this.results.innerHTML = html;
    this.results.hidden = html === '';
    this.input.setAttribute('aria-expanded', String(html !== ''));
    this.input.removeAttribute('aria-activedescendant');
  }

  close() {
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  onKeydown(event) {
    if (this.results.hidden) return;
    const items = this.options();
    if (!items.length) return;
    const current = items.findIndex((el) => el.getAttribute('aria-selected') === 'true');
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (current + step + items.length) % items.length;
      items.forEach((el, i) => el.setAttribute('aria-selected', String(i === next)));
      this.input.setAttribute('aria-activedescendant', items[next].id);
      items[next].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && current > -1) {
      event.preventDefault();
      const link = items[current].querySelector('a');
      if (link) window.location.href = link.href;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Keyboard access to horizontal scroll areas without focusable content       */
/* (mobile carousels, announcement track) — WCAG 2.1.1                         */
/* -------------------------------------------------------------------------- */
const SCROLL_AREAS = '.item-grid[data-mobile="scroll"], [data-carousel-track], .scroller';
function syncScrollAreas(root = document) {
  root.querySelectorAll(SCROLL_AREAS).forEach((el) => {
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    const hasFocusable = Boolean(el.querySelector(FOCUSABLE));
    if (scrollable && !hasFocusable) {
      el.setAttribute('tabindex', '0');
      if (!el.hasAttribute('role')) el.setAttribute('role', 'region');
      if (!el.hasAttribute('aria-label')) {
        const heading = el.closest('section, [class*="section"]')?.querySelector('h1, h2, h3');
        el.setAttribute('aria-label', heading?.textContent.trim() || document.title);
      }
    } else if (el.getAttribute('tabindex') === '0' && !scrollable) {
      el.removeAttribute('tabindex');
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Registration + editor lifecycle                                            */
/* -------------------------------------------------------------------------- */
const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
define('sticky-header', StickyHeader);
define('drawer-panel', DrawerPanel);
define('details-dropdown', DetailsDropdown);
define('accordion-item', AccordionItem);
define('carousel-slider', CarouselSlider);
define('localization-form', LocalizationForm);
define('video-facade', VideoFacade);
define('share-button', ShareButton);
define('marquee-band', MarqueeBand);
define('product-recommendations', ProductRecommendations);
define('recently-viewed', RecentlyViewed);
define('routine-add-all', RoutineAddAll);
define('predictive-search', PredictiveSearch);

scanReveal();
syncScrollAreas();
let scrollAreasFrame;
window.addEventListener('resize', () => {
  cancelAnimationFrame(scrollAreasFrame);
  scrollAreasFrame = requestAnimationFrame(() => syncScrollAreas());
}, { passive: true });

document.addEventListener('shopify:section:load', (event) => {
  scanReveal(event.target);
  syncScrollAreas(event.target);
});

// Selecting a block inside a closed drawer or accordion opens it in the editor.
document.addEventListener('shopify:block:select', (event) => {
  event.target.closest('drawer-panel')?.open();
  event.target.closest('accordion-item')?.open();
  if (event.target.matches('accordion-item')) event.target.open();
});
document.addEventListener('shopify:section:select', (event) => {
  scanReveal(event.target);
});
